import type { PageResponse } from '@omnia/ui/src/lib'
import { sanitizeSiteTitle } from '@omnia/ui/src/lib'
import type { FC } from 'react'
import { Helmet } from 'react-helmet'
import { cmsDataService } from '@/common/services/cms.data.service'

interface MetaProps extends Omit<PageResponse, 'pageHeader'> {}

// TODO: Move to omnia core?
// eslint-disable-next-line complexity -- todo
export const Meta: FC<MetaProps> = page => {
    if (!page) return null

    const siteTitle = cmsDataService.get().siteTitle
    const title = siteTitle + ' - ' + (page.seoPageTitle || page.name)
    const metaKeywords = page.metaKeywords

    // eslint-disable-next-line no-restricted-properties -- we can use document.location for SEO purposes
    const url = page.seoUrl || document.location.origin + document.location.pathname

    // eslint-disable-next-line no-restricted-properties -- we can use document.location for SEO purposes
    const imageUrl = page.seoImage && page.seoImage.mobile ? document.location.origin + page.seoImage.mobile : null

    const { robotsNoFollow: nofollow, robotsNoIndex: noindex } = page
    const robotContent = Object.entries({ noindex, nofollow })
        .filter(r => r[1])
        .map(r => r[0])
        .join(', ')

    const metaDescription = page.metaDescription

    const canonicalUrl: string | undefined = page.masterCanonicalUrl?.url
        ? window.origin + page.masterCanonicalUrl.url
        : undefined
    const pageUrl: string = canonicalUrl ? canonicalUrl : window.origin + page.url

    return (
        <Helmet>
            <title>{title}</title>
            <meta name="keywords" content={metaKeywords} />
            <meta name="description" content={metaDescription} />
            {/* <meta name="copyright" content={cmsDataService.get().footer.copyrightText} /> */}

            {/* Robot meta-tag */}
            {robotContent.length > 0 && <meta name="robots" content={robotContent} />}

            {/* OpenGraph data */}
            <meta property="og:title" content={title} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:url" content={url} />
            {imageUrl && <meta property="og:image" content={imageUrl} />}
            {imageUrl && <meta property="og:image:width" content="1200" />}
            {imageUrl && <meta property="og:image:height" content="630" />}
            {page.oGType && <meta property="og:type" content={page.oGType} />}
            {page.oGLocale && <meta property="og:locale" content={page.oGLocale} />}

            {/* Twitter data */}
            <meta name="twitter:site" content={sanitizeSiteTitle(cmsDataService.get().siteTitle)} />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={metaDescription} />
            {page.twitterCard && <meta name="twitter:card" content={page.twitterCard} />}
            {imageUrl && <meta name="twitter:image" content={imageUrl} />}
            {page.twitterCard && <meta name="twitter:creator" content={page.twitterCard} />}

            {/* Schema data */}
            <meta itemProp="name" content={title} />
            <meta itemProp="description" content={metaDescription} />

            {/* SEO Canonical */}
            {!page.excludeCanonicalUrl && <link rel="canonical" href={pageUrl} />}

            {imageUrl && <meta itemProp="image" content={imageUrl} />}
        </Helmet>
    )
}
